import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ClipboardList, ShieldCheck, MessageCircle } from "lucide-react";

const CHAT_Q = "/chat?q=" + encodeURIComponent("Behöver jag en certifierad kontrollansvarig för mitt projekt, eller räcker egenkontroll?");

export const KaComparison = ({ hideTemplatesCta = false, compact = false }) => (
  <section className={compact ? "my-2" : "my-12"} data-testid="ka-comparison">
    <h2 className={`${compact ? "text-lg" : "text-2xl sm:text-3xl"} font-semibold tracking-tight text-zinc-900`} style={{ fontFamily: "Outfit" }}>
      Behöver du en Kontrollansvarig (KA)?
    </h2>
    {!compact && (
      <p className="mt-2 text-zinc-600 max-w-3xl">
        Beroende på vad du ska bygga ser kraven olika ut. Här ser du vad som gäller för ditt projekt:
      </p>
    )}

    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Option A */}
      <div className="bg-emerald-50/50 border border-emerald-200 rounded-sm p-6 flex flex-col" data-testid="ka-card-egenkontroll">
        <div className="flex items-center gap-2 text-emerald-700">
          <ClipboardList className="w-5 h-5 stroke-[1.5]" />
          <span className="text-xs uppercase tracking-[0.15em] font-semibold">Mindre projekt</span>
        </div>
        <h3 className="mt-3 text-xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Egenkontroll – du är din egen KA</h3>
        <p className="mt-2 text-sm text-zinc-600">
          <b>Gäller oftast för:</b> Attefallshus, mindre tillbyggnader, garage, altaner eller enklare ändringar.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-zinc-700 flex-1">
          <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Kommunen kräver oftast <b>inte</b> certifierad KA – du som byggherre står för kontrollen själv.</li>
          <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Upprätta en <b>kontrollplan</b> – en checklista över vad som ska kontrolleras under bygget.</li>
          <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Utför egenkontroll och bocka av punkterna allt eftersom arbetet framskrider.</li>
          <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Ta foton vid kritiska moment (armering, isolering, fuktskydd) inför slutbeskedet.</li>
        </ul>
        <div className="mt-4 pt-4 border-t border-emerald-200 text-sm">
          <div><span className="text-zinc-500">Kostnad:</span> <b className="text-emerald-700">0 kr i konsultarvode</b></div>
          <div className="mt-1 text-zinc-600">Bygglovskompassen hjälper dig med färdiga kontrollplansmallar och granskning av dina ritningar innan du skickar in.</div>
        </div>
      </div>

      {/* Option B */}
      <div className="bg-blue-50/50 border border-blue-200 rounded-sm p-6 flex flex-col" data-testid="ka-card-certifierad">
        <div className="flex items-center gap-2 text-blue-700">
          <ShieldCheck className="w-5 h-5 stroke-[1.5]" />
          <span className="text-xs uppercase tracking-[0.15em] font-semibold">Större projekt</span>
        </div>
        <h3 className="mt-3 text-xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Certifierad Kontrollansvarig</h3>
        <p className="mt-2 text-sm text-zinc-600">
          <b>Gäller oftast för:</b> Nybyggnad av villa/enbostadshus, större ombyggnader eller när bärande konstruktion/brandskydd påverkas påtagligt.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-zinc-700 flex-1">
          <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" /> <span><b>Lagkrav enligt PBL</b> att anlita en oberoende, certifierad KA.</span></li>
          <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" /> Upprättar den formella kontrollplanen tillsammans med dig.</li>
          <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" /> Deltar vid kommunens tekniska samråd och slutsamråd.</li>
          <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" /> Gör arbetsplatsbesök och skriver slututlåtandet inför ditt slutbesked.</li>
        </ul>
        <div className="mt-4 pt-4 border-t border-blue-200 text-sm">
          <div><span className="text-zinc-500">Uppskattad kostnad:</span> <b className="text-blue-700">ca 15 000 – 40 000 kr</b> <span className="text-zinc-500">(beroende på projekt och plats)</span></div>
          <div className="mt-1 text-zinc-600">KA måste vara certifierad av Kiwa eller RISE och får inte vara släkt med dig eller den som bygger.</div>
        </div>
      </div>
    </div>

    <div className="mt-4 flex flex-wrap gap-3">
      {!hideTemplatesCta && (
        <Link to="/guide/templates" data-testid="ka-cta-kontrollplan"
          className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-3 rounded-sm text-sm font-medium active:scale-[0.98] transition">
          <ClipboardList className="w-4 h-4" /> Hämta kontrollplan för egenkontroll (gratis)
        </Link>
      )}
      <Link to={CHAT_Q} data-testid="ka-cta-chat"
        className="inline-flex items-center gap-2 bg-white hover:bg-zinc-50 text-zinc-900 border border-zinc-300 px-5 py-3 rounded-sm text-sm font-medium transition">
        <MessageCircle className="w-4 h-4 text-blue-700" /> Fråga AI-rådgivaren om PBL & BBR <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

export default KaComparison;
